import type {
  MemberPrepaidSessionOpenPreview,
  MemberPrepaidSessionOpenRequest,
  MemberReadModel,
  PriceSnapshot,
  TableSnapshot,
} from "@billiard/contracts";
import { isMemberPrepaidSessionOpenRequest } from "@billiard/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  ApiRequestError,
  getMemberPrepaidSessionOpenResult,
  listMembers,
  openMemberPrepaidSession,
  previewMemberPrepaidSessionOpen,
} from "./api";

function money(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function recoveryKey(operator: { tenantId: string; storeId: string; id: string }): string {
  return `billiard.member-prepaid-open.v1:${operator.tenantId}:${operator.storeId}:${operator.id}`;
}

function readRecovery(key: string): MemberPrepaidSessionOpenRequest | null {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isMemberPrepaidSessionOpenRequest(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveRecovery(key: string, command: MemberPrepaidSessionOpenRequest): boolean {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(command));
    return readRecovery(key)?.idempotencyKey === command.idempotencyKey;
  } catch {
    return false;
  }
}

function clearRecovery(key: string, command: MemberPrepaidSessionOpenRequest): void {
  if (readRecovery(key)?.idempotencyKey !== command.idempotencyKey) return;
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // A completed server result remains authoritative even if local cleanup fails.
  }
}

const protectedPrepaidOpenCodes = new Set([
  "MEMBER_PREPAID_OPEN_CLOUD_RESULT_UNKNOWN",
  "MEMBER_PREPAID_OPEN_REVIEW_REQUIRED",
  "MEMBER_PREPAID_OPEN_LOCAL_FINALIZE_CONFLICT",
  "MEMBER_PREPAID_OPEN_TRANSACTION_CONFLICT",
  "MEMBER_PREPAID_OPEN_CLOUD_RECEIPT_INVALID",
  "MEMBER_PREPAID_OPEN_NOT_CONFIRMED",
  "MEMBER_PREPAID_OPEN_OPERATION_TRACE_MISSING",
]);

function requiresRecoveryProtection(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  if (protectedPrepaidOpenCodes.has(error.code)) return true;
  return error.statusCode === undefined || error.statusCode < 400 || error.statusCode >= 500;
}

function protectedFailureMessage(error: unknown, repeated: boolean): string {
  if (
    error instanceof ApiRequestError &&
    (error.code === "MEMBER_PREPAID_OPEN_REVIEW_REQUIRED" ||
      error.code === "MEMBER_PREPAID_OPEN_LOCAL_FINALIZE_CONFLICT")
  ) {
    return `${error.message} 原会员、金额、球台和请求号继续保留，请勿另收款或另开台。`;
  }
  return repeated
    ? "原请求结果仍无法确认，保护记录继续保留；请恢复连接后再次使用原请求查询。"
    : "扣款或开台结果暂时无法确认。原会员、金额、球台和请求号已保留，只能恢复原请求。";
}

export function MemberPrepaidOpenPanel({
  operator,
  table,
  pricingPlan,
  disabled,
  onCompleted,
}: {
  operator: { tenantId: string; storeId: string; id: string };
  table: TableSnapshot;
  pricingPlan: PriceSnapshot;
  disabled: boolean;
  onCompleted: (snapshot: TableSnapshot) => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const contextKey = useMemo(() => recoveryKey(operator), [operator]);
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState<MemberReadModel[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [preview, setPreview] = useState<MemberPrepaidSessionOpenPreview | null>(null);
  const [recovery, setRecovery] = useState<MemberPrepaidSessionOpenRequest | null>(() =>
    readRecovery(contextKey),
  );
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setMembers([]);
    setSelectedMemberId("");
    setPreview(null);
    setRecovery(readRecovery(contextKey));
    setMessage(null);
  }, [contextKey]);

  const recoveryMatches = Boolean(
    recovery &&
      recovery.preview.tenantId === operator.tenantId &&
      recovery.preview.storeId === operator.storeId &&
      recovery.preview.tableId === table.tableId,
  );

  const finish = async (
    snapshot: TableSnapshot,
    command: MemberPrepaidSessionOpenRequest,
  ): Promise<void> => {
    clearRecovery(contextKey, command);
    setRecovery(null);
    queryClient.setQueryData<TableSnapshot[]>(["tables"], (current) =>
      current?.map((item) => (item.tableId === snapshot.tableId ? snapshot : item)),
    );
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["tables"] }),
      queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
      queryClient.invalidateQueries({ queryKey: ["orders"] }),
      queryClient.invalidateQueries({ queryKey: ["connectivity"] }),
    ]);
    onCompleted(snapshot);
  };

  const findMembers = async (): Promise<void> => {
    setPending(true);
    setMessage(null);
    try {
      const result = await listMembers(search.trim());
      setMembers(result.slice(0, 20));
      if (!result.some((member) => member.id === selectedMemberId)) {
        setSelectedMemberId("");
        setPreview(null);
      }
      if (result.length === 0) setMessage("没有找到可用会员，请核对会员号、姓名或手机号。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "会员查询失败，请稍后重试。");
    } finally {
      setPending(false);
    }
  };

  const loadPreview = async (): Promise<void> => {
    if (!selectedMemberId) return;
    setPending(true);
    setMessage(null);
    try {
      const result = await previewMemberPrepaidSessionOpen({
        tableId: table.tableId,
        expectedTableRevision: table.revision,
        pricingPlanId: pricingPlan.id,
        expectedPricingPlanVersion: pricingPlan.version,
        memberId: selectedMemberId,
      });
      setPreview(result);
    } catch (error) {
      setPreview(null);
      setMessage(error instanceof Error ? error.message : "预付金额核对失败，请重新读取。");
    } finally {
      setPending(false);
    }
  };

  const confirm = async (): Promise<void> => {
    if (!preview || Date.parse(preview.expiresAt) <= Date.now()) {
      setPreview(null);
      setMessage("会员余额预览已过期，请重新核对后再确认。");
      return;
    }
    const command: MemberPrepaidSessionOpenRequest = {
      preview,
      idempotencyKey: `member-prepaid-open:${globalThis.crypto.randomUUID()}`,
    };
    if (!saveRecovery(contextKey, command)) {
      setMessage("本机无法保存防重复记录，本次没有扣款或开台。请先检查浏览器存储。");
      return;
    }
    setRecovery(command);
    setPending(true);
    setMessage("正在按已确认金额扣除会员余额并开台，请勿重复点击。");
    try {
      await finish(await openMemberPrepaidSession(command), command);
    } catch (error) {
      const protectedOutcome = requiresRecoveryProtection(error);
      if (!protectedOutcome) {
        clearRecovery(contextKey, command);
        setRecovery(null);
      }
      setMessage(
        protectedOutcome
          ? protectedFailureMessage(error, false)
          : error instanceof Error
            ? error.message
            : "会员预付开台没有执行，请重新核对。",
      );
    } finally {
      setPending(false);
    }
  };

  const recover = async (): Promise<void> => {
    if (!recoveryMatches || !recovery) return;
    setPending(true);
    setMessage("正在先查询原请求结果；没有明确结果前不会生成新请求。");
    try {
      let result: TableSnapshot;
      try {
        result = await getMemberPrepaidSessionOpenResult(recovery);
      } catch (error) {
        if (!(error instanceof ApiRequestError) || error.statusCode !== 404) throw error;
        result = await openMemberPrepaidSession(recovery);
      }
      await finish(result, recovery);
    } catch (error) {
      setMessage(
        requiresRecoveryProtection(error)
          ? protectedFailureMessage(error, true)
          : error instanceof Error
            ? error.message
            : "原请求没有完成，请保留现场并核对。",
      );
    } finally {
      setPending(false);
    }
  };

  if (recovery && !recoveryMatches) {
    return (
      <div className="frontdesk-pricing-blocked" role="alert">
        本机另一个球台有一笔会员预付开台结果待确认。请回到原球台恢复，当前不能发起新扣款。
      </div>
    );
  }

  if (recoveryMatches && recovery) {
    return (
      <section className="frontdesk-pricing-selection" aria-label="会员预付开台恢复">
        <output className="frontdesk-pricing-blocked" role="alert">
          原会员 {recovery.preview.memberDisplayName}（{recovery.preview.memberNumber}）、
          {money(recovery.preview.prepaidFen)} 和 {recovery.preview.tableNumber} 号桌已锁定。
          只能查询并恢复原请求，不能更换会员或金额。
        </output>
        {message ? <output className="dialog-error">{message}</output> : null}
        <button className="button primary" type="button" disabled={pending} onClick={recover}>
          {pending ? "正在确认原请求..." : "查询并恢复原预付开台"}
        </button>
      </section>
    );
  }

  return (
    <section className="frontdesk-pricing-selection" aria-label="会员预付开台">
      <header>
        <div>
          <h3>选择会员并先付包时费</h3>
          <p>系统先显示权威余额和扣款后余额；确认后才扣款并建立台单。</p>
        </div>
      </header>
      <label>
        查找会员
        <input
          value={search}
          maxLength={80}
          disabled={disabled || pending}
          placeholder="输入会员号、姓名或手机号"
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <button
        className="button secondary"
        type="button"
        disabled={disabled || pending}
        onClick={findMembers}
      >
        {pending ? "正在读取..." : "读取会员"}
      </button>
      {members.length > 0 ? (
        <fieldset>
          <legend>本次绑定会员</legend>
          {members.map((member) => (
            <label key={member.id}>
              <input
                type="radio"
                name="member-prepaid-open-member"
                checked={selectedMemberId === member.id}
                disabled={disabled || pending || member.status !== "ACTIVE"}
                onChange={() => {
                  setSelectedMemberId(member.id);
                  setPreview(null);
                }}
              />
              {member.name}（{member.memberNumber}）· 可用余额 {money(member.availableBalanceFen)}
              {member.status === "ACTIVE" ? "" : " · 当前不可用"}
            </label>
          ))}
        </fieldset>
      ) : null}
      <button
        className="button secondary"
        type="button"
        disabled={disabled || pending || !selectedMemberId}
        onClick={loadPreview}
      >
        核对权威余额与扣款结果
      </button>
      {preview ? (
        <output className="frontdesk-pricing-preview" aria-live="polite">
          <strong>
            {preview.memberDisplayName}（{preview.memberNumber}）将预付 {money(preview.prepaidFen)}
          </strong>
          <span>当前权威可用余额：{money(preview.availableBalanceFen)}</span>
          <span>确认扣款后预计余额：{money(preview.balanceAfterPrepaymentFen)}</span>
          <span>扣款成功后立即按“{preview.pricingPlanName}”建立台单并开灯。</span>
        </output>
      ) : null}
      {message ? <output className="dialog-error">{message}</output> : null}
      <button
        className="button primary"
        type="button"
        disabled={disabled || pending || !preview || Date.parse(preview.expiresAt) <= Date.now()}
        onClick={confirm}
      >
        {pending ? "正在处理..." : "确认扣款并开台"}
      </button>
    </section>
  );
}
